import React, { useEffect, useState } from 'react';

/**
 * SplashScreen — pleine page, affichée juste après login pour donner un feeling
 * "OS qui boot". Joue une séquence de boot messages puis appelle onDone.
 *
 * Durée totale ≈ 1.8s.
 */
export default function SplashScreen({ onDone }) {
  const STEPS = [
    { label: 'Connexion à Qonto', accent: '#8C54FF' },
    { label: 'Sync Twilio WhatsApp', accent: '#25D366' },
    { label: 'Chargement Cerveau Boufi AI', accent: '#FF5C00' },
    { label: 'Prêt ✨', accent: '#B8CC5A' },
  ];
  const [stepIdx, setStepIdx] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (stepIdx >= STEPS.length - 1) {
      // Last step is "Prêt"
      const t = setTimeout(() => setDone(true), 350);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStepIdx((i) => i + 1), 420);
    return () => clearTimeout(t);
  }, [stepIdx]);

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => onDone?.(), 380);
      return () => clearTimeout(t);
    }
  }, [done, onDone]);

  return (
    <div
      data-testid="splash-screen"
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${
        done ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{ backgroundColor: '#0A0712' }}
    >
      {/* Floating orbs background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full opacity-50"
          style={{
            background: 'radial-gradient(circle, rgba(140, 84, 255, 0.6) 0%, transparent 65%)',
            filter: 'blur(60px)',
            animation: 'splash-float 6s ease-in-out infinite',
          }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full opacity-40"
          style={{
            background: 'radial-gradient(circle, rgba(184, 204, 90, 0.55) 0%, transparent 65%)',
            filter: 'blur(80px)',
            animation: 'splash-float 8s ease-in-out infinite reverse',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo with pulsing halo */}
        <div className="relative mb-8">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255, 92, 0, 0.45) 0%, transparent 65%)',
              animation: 'splash-pulse 1.6s ease-in-out infinite',
              transform: 'scale(2.5)',
            }}
          />
          <img
            src="/boufi-logo.png"
            alt="Boufi"
            className="h-24 w-auto relative drop-shadow-2xl"
            style={{ animation: 'splash-logo 1.6s ease-in-out infinite' }}
          />
        </div>

        {/* Brand */}
        <h1
          className="text-3xl font-heading font-bold tracking-tight mb-1"
          style={{
            background: 'linear-gradient(135deg, #FFFFFF 0%, #B8CC5A 50%, #FF5C00 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Boufi OS
        </h1>
        <p className="text-white/40 text-xs uppercase tracking-[0.25em] mb-10">v2.6 · production</p>

        {/* Step list */}
        <div className="w-72 space-y-2.5" data-testid="splash-steps">
          {STEPS.map((s, i) => {
            const isPast = i < stepIdx;
            const isCurrent = i === stepIdx && !done;
            const isDone = i === stepIdx && done && i === STEPS.length - 1;
            const visible = isPast || isCurrent || isDone;
            if (!visible) return null;
            return (
              <div
                key={s.label}
                className="flex items-center gap-3 animate-fade-in-up"
                style={{ animationDelay: '0s' }}
              >
                {/* Dot */}
                <div className="relative w-2 h-2 flex-shrink-0">
                  {(isCurrent && !isDone) ? (
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{ backgroundColor: s.accent, animation: 'splash-dot 1s ease-in-out infinite' }}
                    />
                  ) : (
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{ backgroundColor: s.accent, opacity: isPast || isDone ? 0.9 : 0.3 }}
                    />
                  )}
                </div>
                <span
                  className="text-sm"
                  style={{
                    color: isCurrent ? '#FFFFFF' : isPast || isDone ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.3)',
                    fontWeight: isCurrent ? 500 : 400,
                  }}
                >
                  {s.label}
                </span>
                {(isPast || isDone) && (
                  <svg
                    className="w-3.5 h-3.5 ml-auto"
                    fill="none"
                    stroke={s.accent}
                    strokeWidth="3"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes splash-pulse {
          0%, 100% { opacity: 0.4; transform: scale(2.3); }
          50% { opacity: 0.65; transform: scale(2.7); }
        }
        @keyframes splash-logo {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        @keyframes splash-dot {
          0%, 100% { opacity: 0.5; transform: scale(0.8); box-shadow: 0 0 0 0 currentColor; }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes splash-float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(30px, 20px); }
        }
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fade-in-up 0.35s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </div>
  );
}
